import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { api } from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';
import TimetableView from './TimetableView';
import { TimetableSlot } from '../types';

function normalize(s: string): string {
  return (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function mapType(tip: string | null | undefined): TimetableSlot['type'] {
  const t = normalize(tip || '');
  if (t.includes('lab')) return 'lab';
  if (t.includes('laborator')) return 'lab';
  if (t.includes('seminar') || t.includes('tutorial')) return 'tutorial';
  return 'lecture';
}

function lessonTypeToSlotType(t: string | null | undefined): TimetableSlot['type'] {
  const norm = normalize(t || '');
  if (norm === 'lab') return 'lab';
  if (norm === 'tutorial') return 'tutorial';
  if (norm === 'lecture') return 'lecture';
  return mapType(t);
}

type TimetableInfo = { id: number; name: string; created_by: number; classroom_id: number };
type TimetableEntry = {
  id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
  subject: string;
  room?: string | null;
  lesson_type?: string | null;
  teacher_id?: number;
  teacher_name?: string | null;
};

function entryToSlot(entry: TimetableEntry): TimetableSlot {
  return {
    id: String(entry.id),
    day: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'][entry.day_of_week] || 'Monday',
    startTime: entry.start_time,
    endTime: entry.end_time,
    subject: entry.subject || '—',
    room: entry.room || '',
    type: lessonTypeToSlotType(entry.lesson_type),
    teacherName: entry.teacher_name || null,
  };
}

interface Props {
  classId: number;
}

type GradebookConfigLite = {
  id: number;
  school_year: string;
  structure: string;
};

function cfgLabel(cfg: GradebookConfigLite) {
  const s = (cfg.structure || '').toLowerCase();
  const struct = s === 'semestru' ? 'Semestru' : s === 'trimestru' ? 'Trimestru' : 'Module';
  return `${cfg.school_year} • ${struct}`;
}

const OrarPanel: React.FC<Props> = ({ classId }) => {
  const { me } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [configs, setConfigs] = useState<GradebookConfigLite[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<number | null>(null);
  const [timetables, setTimetables] = useState<(TimetableInfo & { gradebook_config_id?: number; period_index?: number })[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [entries, setEntries] = useState<TimetableEntry[]>([]);
  const [mySubjects, setMySubjects] = useState<string[]>([]);

  const [cellOpen, setCellOpen] = useState(false);
  const [cellDay, setCellDay] = useState<string>('Monday');
  const [cellTime, setCellTime] = useState<string>('08:00');
  const [cellEntry, setCellEntry] = useState<TimetableEntry | null>(null);
  const [cellSubject, setCellSubject] = useState('');
  const [cellRoom, setCellRoom] = useState('');
  const [cellType, setCellType] = useState<TimetableSlot['type']>('lecture');
  const [cellBusy, setCellBusy] = useState(false);
  const [cellError, setCellError] = useState<string | null>(null);

  const slots = useMemo(() => entries.map(entryToSlot), [entries]);
  const selectedTimetable = useMemo(() => timetables.find((t) => t.id === selectedId) || null, [timetables, selectedId]);
  const selectedPeriodIndex = useMemo(() => Number(selectedTimetable?.period_index ?? 0), [selectedTimetable?.period_index]);

  const loadConfigs = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get(`/api/catalog/config/all/${classId}`);
      const list = Array.isArray(resp.data) ? (resp.data as GradebookConfigLite[]) : [];
      setConfigs(list);
      if (!selectedConfigId && list.length) setSelectedConfigId(list[0].id);
      if (selectedConfigId && list.every((c) => c.id !== selectedConfigId)) setSelectedConfigId(list[0]?.id ?? null);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Nu s-a putut incarca lista de cataloage.');
    } finally {
      setLoading(false);
    }
  };

  const loadTimetablesForConfig = async (cfgId: number) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get(`/api/orar/classrooms/${classId}/timetables`, { params: { gradebook_config_id: cfgId } });
      const list = Array.isArray(resp.data)
        ? (resp.data as (TimetableInfo & { gradebook_config_id?: number; period_index?: number })[])
        : [];
      const ordered = list.slice().sort((a, b) => Number(a.period_index ?? 0) - Number(b.period_index ?? 0));
      setTimetables(ordered);
      if (!selectedId && ordered.length) setSelectedId(ordered[0].id);
      if (selectedId && ordered.every((t) => t.id !== selectedId)) setSelectedId(ordered[0]?.id ?? null);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Nu s-a putut incarca lista de orare.');
    } finally {
      setLoading(false);
    }
  };

  const loadEntries = async (ttId: number) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get(`/api/orar/timetables/${ttId}/entries`);
      setEntries(Array.isArray(resp.data) ? (resp.data as TimetableEntry[]) : []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Nu s-a putut incarca orarul.');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  useEffect(() => {
    if (!selectedConfigId) {
      setTimetables([]);
      setSelectedId(null);
      setEntries([]);
      return;
    }
    void loadTimetablesForConfig(selectedConfigId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConfigId]);

  useEffect(() => {
    if (!selectedId) {
      setEntries([]);
      return;
    }
    void loadEntries(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    if (!selectedConfigId) {
      setMySubjects([]);
      return;
    }
    if (!me?.id) {
      setMySubjects([]);
      return;
    }

    let cancelled = false;
    const loadMySubjects = async () => {
      try {
        const resp = await api.get(`/api/catalog/classroom/${classId}/overview`, {
          params: { config_id: selectedConfigId, period_index: selectedPeriodIndex },
        });
        const subjects = Array.isArray(resp.data?.subjects) ? resp.data.subjects : [];
        const mine = subjects
          .filter((s: any) => Number(s?.professor_id) === Number(me.id))
          .map((s: any) => (s?.name || '').toString().trim())
          .filter((n: string) => !!n);
        const unique = Array.from(new Set(mine)).sort((a, b) => a.localeCompare(b));
        if (!cancelled) setMySubjects(unique);
      } catch {
        if (!cancelled) setMySubjects([]);
      }
    };

    void loadMySubjects();
    return () => {
      cancelled = true;
    };
  }, [classId, me?.id, selectedConfigId, selectedPeriodIndex]);

  const openCell = (day: string, time: string, slot: TimetableSlot | null) => {
    setCellError(null);
    setCellDay(day);
    setCellTime(time);
    const dow = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].indexOf(day);
    const found = entries.find((e) => e.day_of_week === dow && e.start_time === time) || null;
    setCellEntry(found);
    setCellSubject(found?.subject || slot?.subject || '');
    setCellRoom(found?.room || slot?.room || '');
    setCellType(slot?.type || (found ? lessonTypeToSlotType(found.lesson_type) : 'lecture'));
    setCellOpen(true);
  };

  const toEndTime = (start: string) => {
    const m = /^(\d{2}):(\d{2})$/.exec(start);
    if (!m) return start;
    const hh = Number(m[1]);
    const mm = Number(m[2]);
    const d = new Date(2000, 0, 1, hh, mm, 0);
    d.setHours(d.getHours() + 1);
    const h2 = String(d.getHours()).padStart(2, '0');
    const m2 = String(d.getMinutes()).padStart(2, '0');
    return `${h2}:${m2}`;
  };

  const saveCell = async () => {
    if (!selectedId) return;
    const subject = cellSubject.trim();
    if (!subject) {
      setCellError('Completeaza materia.');
      return;
    }
    const dow = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].indexOf(cellDay);
    setCellBusy(true);
    setCellError(null);
    try {
      await api.put(`/api/orar/timetables/${selectedId}/entries/upsert`, {
        day_of_week: dow,
        start_time: cellTime,
        end_time: toEndTime(cellTime),
        subject,
        room: cellRoom.trim() || null,
        lesson_type: cellType,
      });
      await loadEntries(selectedId);
      setCellOpen(false);
    } catch (err: any) {
      setCellError(err?.response?.data?.detail || 'Eroare la salvare.');
    } finally {
      setCellBusy(false);
    }
  };

  const deleteCell = async () => {
    if (!selectedId) return;
    const dow = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].indexOf(cellDay);
    if (!window.confirm('Stergi aceasta lectie?')) return;
    setCellBusy(true);
    setCellError(null);
    try {
      await api.delete(`/api/orar/timetables/${selectedId}/entries`, { params: { day_of_week: dow, start_time: cellTime } });
      await loadEntries(selectedId);
      setCellOpen(false);
    } catch (err: any) {
      setCellError(err?.response?.data?.detail || 'Eroare la stergere.');
    } finally {
      setCellBusy(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground">Se încarcă orarul…</div>;
  }

  if (error) {
    return <div className="bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{error}</div>;
  }

  return (
    <div className="space-y-3">
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
          <div className="flex-1">
            <div className="text-sm font-semibold text-foreground mb-1">Orar (dupa catalog)</div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <select
                value={selectedConfigId ?? ''}
                onChange={(e) => setSelectedConfigId(e.target.value ? Number(e.target.value) : null)}
                className="w-full sm:w-auto px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={!configs.length}
              >
                {configs.length === 0 ? (
                  <option value="">-- niciun catalog --</option>
                ) : (
                  configs.map((c) => (
                    <option key={c.id} value={c.id}>
                      {cfgLabel(c)}
                    </option>
                  ))
                )}
              </select>
            </div>

            {timetables.length > 0 && (
              <div className="mt-3">
                <div className="sm:hidden flex items-center gap-2">
                  <Icon name="AlignJustify" size={18} className="text-muted-foreground flex-shrink-0" />
                  <select
                    value={selectedId ?? ''}
                    onChange={(e) => setSelectedId(e.target.value ? Number(e.target.value) : null)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    disabled={!timetables.length}
                  >
                    {timetables.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="hidden sm:block overflow-x-auto">
                  <div className="inline-flex items-center gap-2 min-w-max">
                    {timetables.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setSelectedId(t.id)}
                        className={`px-3 py-1.5 rounded-md border text-sm transition-colors duration-150 ${
                          selectedId === t.id
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background border-border hover:bg-muted/40'
                        }`}
                        type="button"
                        title={t.name}
                      >
                        {t.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            Click pe o celula ca sa adaugi/modifici.
          </div>
        </div>
      </div>

      {configs.length === 0 ? (
        <div className="bg-muted/30 border border-border rounded-md p-3 text-sm text-muted-foreground flex items-start gap-2">
          <Icon name="Info" size={18} className="mt-0.5" />
          <div>Creeaza intai un catalog (an scolar + structura). Orarele se genereaza automat dupa catalog.</div>
        </div>
      ) : null}

      <TimetableView
        slots={slots}
        onEditClick={() => window.alert('Click pe celule pentru a adauga sau modifica lectii.')}
        onSlotClick={() => {}}
        editable
        onCellClick={openCell}
      />

      {cellOpen && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50 animate-fade-in">
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-xl overflow-hidden animate-scale-in">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">
                {cellEntry ? 'Editeaza lectia' : 'Adauga lectie'} • {cellDay} {cellTime}
              </h3>
              <button
                className="p-2 rounded-md hover:bg-muted transition-colors duration-150"
                onClick={() => setCellOpen(false)}
                aria-label="Inchide"
              >
                <Icon name="X" size={18} className="text-muted-foreground" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {cellError && <div className="bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{cellError}</div>}

              {cellEntry?.teacher_name && (
                <div className="text-xs text-muted-foreground">
                  Profesor: <span className="text-foreground">{cellEntry.teacher_name}</span>
                </div>
              )}

              <Input
                label="Materie *"
                value={cellSubject}
                onChange={(e) => setCellSubject(e.target.value)}
                disabled={cellBusy}
                list="orar-my-subjects"
                placeholder={mySubjects.length ? 'Alege din materiile tale…' : 'Scrie materia…'}
              />
              <datalist id="orar-my-subjects">
                {mySubjects.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="Sala (optional)" value={cellRoom} onChange={(e) => setCellRoom(e.target.value)} disabled={cellBusy} />
                <label className="block text-sm font-medium text-foreground">
                  Tip
                  <select
                    value={cellType}
                    onChange={(e) => setCellType(e.target.value as any)}
                    className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    disabled={cellBusy}
                  >
                    <option value="lecture">Curs</option>
                    <option value="lab">Laborator</option>
                    <option value="tutorial">Seminar</option>
                  </select>
                </label>
              </div>

              <div className="flex items-center justify-end gap-2 pt-1">
                {cellEntry && (
                  <Button variant="destructive" onClick={deleteCell} disabled={cellBusy}>
                    Sterge
                  </Button>
                )}
                <Button variant="outline" onClick={() => setCellOpen(false)} disabled={cellBusy}>
                  Anuleaza
                </Button>
                <Button variant="default" onClick={saveCell} disabled={cellBusy}>
                  Salveaza
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrarPanel;
